import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, extractError, API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { FileSignature, CheckCircle2, Clock, Download } from "lucide-react";

export default function AgencyContract() {
  const [agency, setAgency] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [form, setForm] = useState({ signature_name: "", accept_terms: false });
  const load = () => api.get("/agency/me").then((r) => {
    setAgency(r.data);
    setForm((f) => ({ ...f, signature_name: `${r.data.representative_first_name || ""} ${r.data.representative_last_name || ""}`.trim() }));
  }).catch((e) => toast.error(extractError(e)));
  useEffect(() => { load(); }, []);

  const contract = agency?.partnership_contract || {};
  const hasContract = !!contract.contract_upload_id;
  const isAccepted = !!contract.agency_accepted_at;
  const isValidated = !!contract.admin_validated_at;
  const pdfUrl = hasContract ? `${API_BASE.replace(/\/api$/, "")}/api/agencies/${agency.id}/partnership-contract.pdf` : "";
  const token = localStorage.getItem("mbk_token");
  const authedPdfUrl = pdfUrl ? `${pdfUrl}?_t=${encodeURIComponent(token)}` : "";

  const downloadPdf = async () => {
    try {
      const res = await fetch(pdfUrl, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("Téléchargement échoué");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `contrat-partenariat-${agency.name.replace(/\s+/g, "-")}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.message); }
  };

  const accept = async () => {
    if (!form.accept_terms) { toast.error("Veuillez cocher la case d'acceptation"); return; }
    if (!form.signature_name.trim()) { toast.error("Nom du représentant requis"); return; }
    setAccepting(true);
    try {
      await api.post("/agency/partnership-contract/accept", form);
      toast.success("Contrat accepté — validation finale MOBIKAR en cours");
      load();
    } catch (e) { toast.error(extractError(e)); }
    finally { setAccepting(false); }
  };

  if (!agency) return <div className="text-center text-gray-400 py-24">Chargement…</div>;

  return (
    <div data-testid="agency-contract">
      <div className="flex items-center gap-2">
        <FileSignature className="h-6 w-6 text-[#0e7a3f]" />
        <h1 className="font-display text-3xl font-black text-[#0a1e3f]">Mon contrat de partenariat</h1>
      </div>
      <p className="text-sm text-gray-500 mt-1">Consultez et acceptez électroniquement votre contrat MOBIKAR.</p>

      {!hasContract && (
        <div className="mt-8 bg-white rounded-2xl p-8 border border-gray-100 mbk-card-shadow text-center">
          <Clock className="h-12 w-12 text-amber-500 mx-auto" />
          <div className="font-display text-xl font-black text-[#0a1e3f] mt-4">Contrat non encore généré</div>
          <p className="text-sm text-gray-500 mt-2 max-w-md mx-auto">MOBIKAR est en train de vérifier votre dossier. Votre contrat sera généré dès que vos documents seront validés. Vous recevrez un email de notification.</p>
        </div>
      )}

      {hasContract && (
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 bg-white rounded-2xl p-6 border border-gray-100 mbk-card-shadow">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="font-display text-lg font-black text-[#0a1e3f]">Contrat de partenariat</div>
                <div className="text-xs text-gray-500">Généré le {new Date(contract.generated_at).toLocaleString("fr-FR")}</div>
              </div>
              <Button onClick={downloadPdf} data-testid="download-contract" className="mbk-btn-primary text-white h-10 px-4 rounded-full gap-2"><Download className="h-4 w-4" /> Télécharger PDF</Button>
            </div>
            <iframe title="Contrat de partenariat" src={authedPdfUrl} className="w-full h-[600px] rounded-lg border border-gray-200 bg-gray-50" />
            <p className="text-xs text-gray-500 mt-3">Si l'aperçu ne s'affiche pas, utilisez le bouton "Télécharger PDF" ci-dessus.</p>
          </div>

          <div className="space-y-6">
            {isValidated ? (
              <div className="bg-[#dcfce7] rounded-2xl p-6 border border-[#0e7a3f]/20">
                <CheckCircle2 className="h-8 w-8 text-[#0e7a3f]" />
                <div className="font-display text-lg font-black text-[#0a1e3f] mt-3">Partenariat activé</div>
                <p className="text-sm text-[#0a1e3f]/80 mt-2">Votre partenariat a été validé par MOBIKAR le {new Date(contract.admin_validated_at).toLocaleDateString("fr-FR")}. Vous pouvez maintenant gérer vos véhicules et recevoir des réservations.</p>
              </div>
            ) : isAccepted ? (
              <div className="bg-[#0e7a3f]/5 rounded-2xl p-6 border border-[#0e7a3f]/20">
                <CheckCircle2 className="h-8 w-8 text-[#0e7a3f]" />
                <div className="font-display text-lg font-black text-[#0a1e3f] mt-3">Contrat accepté</div>
                <p className="text-sm text-gray-700 mt-2">Accepté le {new Date(contract.agency_accepted_at).toLocaleString("fr-FR")} par <b>{contract.agency_signature_name}</b>.</p>
                <p className="text-xs text-gray-500 mt-2">MOBIKAR procède à la validation finale de votre partenariat.</p>
              </div>
            ) : (
              <div className="bg-white rounded-2xl p-6 border border-gray-100 mbk-card-shadow">
                <div className="font-display text-lg font-black text-[#0a1e3f]">Accepter le contrat</div>
                <p className="text-sm text-gray-500 mt-1">Après lecture, saisissez votre nom complet et cochez la case pour valider votre consentement.</p>
                <div className="mt-4 space-y-4">
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-gray-500 font-bold">Nom complet du représentant</Label>
                    <Input data-testid="contract-signature" value={form.signature_name} onChange={(e) => setForm((f) => ({ ...f, signature_name: e.target.value }))} className="mt-2 h-11" />
                  </div>
                  <div className="flex items-start gap-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                    <Checkbox data-testid="contract-accept-check" checked={form.accept_terms} onCheckedChange={(v) => setForm((f) => ({ ...f, accept_terms: !!v }))} className="mt-0.5" />
                    <label className="text-xs text-amber-900 leading-relaxed">Je reconnais avoir lu et accepté l'intégralité des clauses du présent contrat de partenariat MOBIKAR. Je certifie être habilité(e) à représenter légalement l'agence.</label>
                  </div>
                  <div className="text-[10px] text-gray-500 italic">Consentement électronique manuel : votre IP, l'horodatage et votre nom seront enregistrés. Ceci ne constitue pas une signature électronique qualifiée eIDAS.</div>
                  <Button data-testid="contract-accept-btn" onClick={accept} disabled={accepting || !form.accept_terms || !form.signature_name.trim()} className="mbk-btn-green w-full text-white h-12 rounded-full font-semibold">
                    {accepting ? "Envoi…" : "Accepter le contrat"}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
